import { useTenantFeatures } from '../../shared/use-tenant-features';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, HandHelping } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MyIncident, MyIncidentList } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { Button, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/** Hoe vaak de app vraagt of er een noodmelding loopt. */
const POLL_INTERVAL_MS = 15_000;

/**
 * De noodmelding over de hele app (Stap 35).
 *
 * Dit hangt in de app-zone zelf en niet op het beginscherm, en dat is een bewuste keuze: een
 * ontruiming mag niemand missen omdat hij net in een gesprek zat te lezen. Loopt er iets, dan
 * verdwijnt de app eronder.
 *
 * **Er is geen websocket.** Stap 30 (realtime) is vervallen, dus de app vraagt het elke vijftien
 * seconden. De push is het snelle kanaal — die komt binnen enkele seconden aan en opent de app;
 * dit pollen is het net eronder voor wie de app al open had of push uit heeft staan. Vijftien
 * seconden is daarmee de traagste weg naar binnen en niet de gewone. Genoteerd in
 * `docs/product/demo-schuld.md`.
 *
 * Eén melding tegelijk op het scherm: de server staat maar één lopend incident per bedrijf toe.
 */
export function EmergencyOverlay() {
  const { api } = useSession();
  const { isOn } = useTenantFeatures();

  const incidents = useQuery({
    queryKey: ['me', 'emergency'],
    // Een bedrijf zonder noodkanaal (ORG-1) vraagt er ook niet elk kwartier naar.
    enabled: isOn('emergency.channel'),
    queryFn: async (): Promise<MyIncident[]> => {
      const { data, error } = await api.GET('/api/v1/me/emergency/incidents', {});
      // Staat de module uit, dan geeft de server een 404 en hoort de app gewoon te werken.
      if (error || !data) return [];
      return (data as MyIncidentList).items;
    },
    refetchInterval: POLL_INTERVAL_MS,
    // Ook bij een scherm dat op de achtergrond stond: iemand die zijn telefoon weer oppakt, moet
    // het meteen zien en niet pas na de volgende ronde.
    refetchOnWindowFocus: true,
  });

  const incident = incidents.data?.[0];
  if (!incident) return null;

  return <EmergencyFullScreen incident={incident} />;
}

function EmergencyFullScreen({ incident }: { incident: MyIncident }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const respond = useMutation({
    mutationFn: async (status: 'safe' | 'help') => {
      const { error } = await api.POST('/api/v1/me/emergency/incidents/{incidentId}/respond', {
        params: { path: { incidentId: incident.id } },
        body: { status, note: '' },
      });
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me', 'emergency'] }),
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="emergency-title"
      data-testid="emergency-overlay"
      // Rood vlak over alles heen. De oefening is oranje: wie een oefening niet kan onderscheiden
      // van een echt incident, leert een echt incident te negeren.
      className={`fixed inset-0 z-50 flex flex-col overflow-y-auto px-element py-section ${
        incident.isDrill ? 'bg-brand-orange-deep' : 'bg-danger'
      }`}
    >
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-section">
        <div className="flex items-center gap-element text-text-on-dark">
          <AlertTriangle aria-hidden className="h-8 w-8 shrink-0" strokeWidth={2.5} />
          <p className="font-heading text-title-md font-semibold uppercase tracking-wide">
            {incident.isDrill ? t('emergency.drillBanner') : t('emergency.banner')}
          </p>
        </div>

        <div>
          <h1 id="emergency-title" className="font-heading text-hero font-bold text-text-on-dark">
            {incident.title}
          </h1>
          <p className="mt-element whitespace-pre-wrap text-title-md text-text-on-dark">
            {incident.instruction}
          </p>
        </div>

        {incident.myStatus ? (
          // Geantwoord, maar het scherm blijft staan: de instructie is niet minder waar nu jij
          // veilig bent. En het antwoord is te wijzigen — wie eerst "veilig" meldde en daarna
          // onder een pallet terechtkomt, moet "hulp nodig" kunnen kiezen (ADR-0060).
          <div className="mt-auto flex flex-col gap-element">
            <p
              data-testid="emergency-answered"
              className="rounded-md bg-surface/15 p-element text-center text-content font-semibold text-text-on-dark"
            >
              {incident.myStatus === 'safe'
                ? t('emergency.answeredSafe')
                : t('emergency.answeredHelp')}
            </p>
            <Button
              variant="secondary"
              size="lg"
              loading={respond.isPending}
              onClick={() => respond.mutate(incident.myStatus === 'safe' ? 'help' : 'safe')}
              data-testid="emergency-change"
            >
              {incident.myStatus === 'safe'
                ? t('emergency.changeToHelp')
                : t('emergency.changeToSafe')}
            </Button>
          </div>
        ) : (
          <div className="mt-auto flex flex-col gap-element">
            {/* Twee knoppen, allebei groot. Op een telefoonscherm in paniek is een derde optie er
                één te veel, en een kleine knop is er geen. */}
            <Button
              size="lg"
              variant="secondary"
              className="h-16 justify-center text-title-md font-semibold"
              loading={respond.isPending}
              onClick={() => respond.mutate('safe')}
              data-testid="emergency-safe"
            >
              <Check aria-hidden className="mr-2 h-6 w-6" strokeWidth={2.5} />
              {t('emergency.iAmSafe')}
            </Button>
            <Button
              size="lg"
              variant="secondary"
              className="h-16 justify-center text-title-md font-semibold"
              loading={respond.isPending}
              onClick={() => respond.mutate('help')}
              data-testid="emergency-help"
            >
              <HandHelping aria-hidden className="mr-2 h-6 w-6" strokeWidth={2.5} />
              {t('emergency.iNeedHelp')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
